import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import MarkerShape from "@mapmycustomers/shared/enum/MarkerShape";
import MarkerSize from "@mapmycustomers/shared/enum/MarkerSize";
import { CategorizedMapEntries, EntityPin } from "@mapmycustomers/shared/types/map";

import ClusterFeature from "@app/scene/map/components/features/ClusterFeature";
import grayscaleClusterStyleGetter from "@app/scene/map/components/features/ClusterFeature/utils/grayscaleClusterStyleGetter";
import EntityFeature from "@app/scene/map/components/features/EntityFeature";
import MultiPinFeature from "@app/scene/map/components/features/MultiPinFeature";
import defaultMultiPinStyleGetter from "@app/scene/map/components/features/MultiPinFeature/utils/defaultMultiPinStyleGetter";
import { getGroupOtherEntries, getSelectedRecords } from "@app/scene/map/store/selectors";
import { getMapSettings } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import colors from "@app/styles/_colors.module.scss";
import { OTHER_PIN_ZINDEX } from "@app/util/map/consts";
import { mapPinIdGetter } from "@app/util/map/idGetters";
import defaultEntityStyleGetter from "@app/util/map/markerStyles/defaultEntityStyleGetter";

interface Props {
  mapSettings: {
    markerSize: MarkerSize;
    showEntityIcons: boolean;
  };
  onEntityPinClick?: (entity: EntityPin) => void;
  onEntityPinDoubleClick?: (entity: EntityPin) => void;
  otherEntries: CategorizedMapEntries;
  selection: Set<string>;
}

const OtherPinsLayer: React.FC<Props> = ({
  mapSettings,
  onEntityPinClick,
  onEntityPinDoubleClick,
  otherEntries,
  selection,
}) => {
  const otherPinColorGetter = useCallback(() => {
    const fillColor = colors.disabledGray;
    return {
      color: colors.white,
      fillColor,
    };
  }, []);

  const otherPinIconGetter = useMemo(
    () => (entityPin: EntityPin) => {
      const pinId = mapPinIdGetter(entityPin);
      const icon = mapSettings.showEntityIcons ? entityPin.data.entity : undefined;
      return selection.has(pinId) ? "selected" : icon;
    },
    [mapSettings.showEntityIcons, selection]
  );

  const otherPinStrokeColorGetter = useMemo(
    () => (entityPin: EntityPin) => {
      const pinId = mapPinIdGetter(entityPin);
      return selection.has(pinId) ? colors.primary100 : undefined;
    },
    [selection]
  );

  const groupOtherPinStyleGetter = useMemo(
    () =>
      defaultEntityStyleGetter({
        colorGetter: otherPinColorGetter,
        icon: otherPinIconGetter,
        markerSize: mapSettings.markerSize,
        shape: MarkerShape.SQUARE,
        showEntityIcon: mapSettings.showEntityIcons,
        strokeColorGetter: otherPinStrokeColorGetter,
        zIndex: OTHER_PIN_ZINDEX,
      }),
    [mapSettings, otherPinColorGetter, otherPinIconGetter, otherPinStrokeColorGetter]
  );

  const groupOtherMultiPinStyleGetter = useMemo(
    () =>
      defaultMultiPinStyleGetter({
        colorGetter: otherPinColorGetter,
        markerSize: mapSettings.markerSize,
        shape: MarkerShape.SQUARE,
        zIndex: OTHER_PIN_ZINDEX,
      }),
    [mapSettings.markerSize, otherPinColorGetter]
  );

  return (
    <>
      <ClusterFeature
        clusters={otherEntries.clusters}
        styleGetter={grayscaleClusterStyleGetter}
        type="otherClusters"
      />

      <MultiPinFeature
        multiPins={otherEntries.multiPins}
        styleGetter={groupOtherMultiPinStyleGetter}
        type="otherMultiPins"
      />

      <EntityFeature
        entityPins={otherEntries.entities}
        onClick={onEntityPinClick}
        onDoubleClick={onEntityPinDoubleClick}
        styleGetter={groupOtherPinStyleGetter}
        type="otherEntities"
      />
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  mapSettings: getMapSettings(state),
  otherEntries: getGroupOtherEntries(state),
  selection: getSelectedRecords(state),
});

export default connect(mapStateToProps)(OtherPinsLayer);
